import {
  SMILE_SIMULATION_CAPTURE_HEIGHT,
  SMILE_SIMULATION_CAPTURE_WIDTH,
  SMILE_SIMULATION_FRAME_ASPECT_RATIO,
} from "../model/constants";
import type {
  SmileSimulationCaptureBounds,
  SmileSimulationFrameSize,
} from "../model/types";

export const getImageFileFromDataUrl = (
  imageDataUrl: string,
  fileName: string,
) => {
  const match = imageDataUrl.match(
    /^data:(image\/[a-zA-Z0-9.+-]+);base64,(.+)$/,
  );

  if (!match) {
    throw new Error("Invalid image data URL");
  }

  const mimeType = match[1];
  const base64Data = match[2];

  if (!mimeType || !base64Data) {
    throw new Error("Invalid image data URL");
  }

  const binaryData = window.atob(base64Data);
  const bytes = Uint8Array.from(binaryData, (character) =>
    character.charCodeAt(0),
  );

  return new File([bytes], fileName, { type: mimeType });
};

const drawSmileSimulationSourceToCanvas = ({
  canvas,
  shouldMirror = false,
  source,
  sourceHeight,
  sourceWidth,
}: {
  canvas: HTMLCanvasElement;
  shouldMirror?: boolean;
  source: CanvasImageSource;
  sourceHeight: number;
  sourceWidth: number;
}) => {
  const {
    sourceX,
    sourceY,
    sourceWidth: croppedSourceWidth,
    sourceHeight: croppedSourceHeight,
    targetWidth,
    targetHeight,
  } = getSmileSimulationCaptureBounds(sourceWidth, sourceHeight);

  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const context = canvas.getContext("2d");

  if (!context) {
    return null;
  }

  if (shouldMirror) {
    context.save();
    context.translate(targetWidth, 0);
    context.scale(-1, 1);
  }

  context.drawImage(
    source,
    sourceX,
    sourceY,
    croppedSourceWidth,
    croppedSourceHeight,
    0,
    0,
    targetWidth,
    targetHeight,
  );

  if (shouldMirror) {
    context.restore();
  }

  return canvas.toDataURL("image/png");
};

const loadSmileSimulationImageElement = (file: File) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    if (!file.type.startsWith("image/")) {
      reject(new Error("Invalid image file"));
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Failed to load uploaded image"));
    };

    image.src = objectUrl;
  });

export const getSmileSimulationUploadImageDataUrl = async (
  file: File,
  canvas: HTMLCanvasElement,
) => {
  const image = await loadSmileSimulationImageElement(file);

  return drawSmileSimulationSourceToCanvas({
    canvas,
    source: image,
    sourceHeight: image.naturalHeight,
    sourceWidth: image.naturalWidth,
  });
};

const getSmileSimulationOpaqueBounds = ({
  context,
  height,
  width,
}: {
  context: CanvasRenderingContext2D;
  height: number;
  width: number;
}) => {
  const imageData = context.getImageData(0, 0, width, height);
  const { data } = imageData;
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const alpha = data[(y * width + x) * 4 + 3];

      if (alpha === 0) {
        continue;
      }

      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
  }

  if (maxX < minX || maxY < minY) {
    return null;
  }

  return {
    height: maxY - minY + 1,
    width: maxX - minX + 1,
    x: minX,
    y: minY,
  };
};

export const createSmileSimulationTrimmedWatermarkBlob = async (file: File) => {
  const image = await loadSmileSimulationImageElement(file);
  const sourceCanvas = document.createElement("canvas");
  sourceCanvas.width = image.naturalWidth;
  sourceCanvas.height = image.naturalHeight;

  const sourceContext = sourceCanvas.getContext("2d");

  if (!sourceContext) {
    throw new Error("Failed to initialize watermark trim canvas");
  }

  sourceContext.drawImage(image, 0, 0, sourceCanvas.width, sourceCanvas.height);

  const bounds = getSmileSimulationOpaqueBounds({
    context: sourceContext,
    height: sourceCanvas.height,
    width: sourceCanvas.width,
  });
  const targetCanvas = document.createElement("canvas");
  const cropX = bounds?.x ?? 0;
  const cropY = bounds?.y ?? 0;
  const cropWidth = bounds?.width ?? sourceCanvas.width;
  const cropHeight = bounds?.height ?? sourceCanvas.height;

  targetCanvas.width = Math.max(1, cropWidth);
  targetCanvas.height = Math.max(1, cropHeight);

  const targetContext = targetCanvas.getContext("2d");

  if (!targetContext) {
    throw new Error("Failed to initialize watermark export canvas");
  }

  targetContext.drawImage(
    sourceCanvas,
    cropX,
    cropY,
    cropWidth,
    cropHeight,
    0,
    0,
    targetCanvas.width,
    targetCanvas.height,
  );

  return new Promise<Blob>((resolve, reject) => {
    targetCanvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Failed to create trimmed watermark image"));
        return;
      }

      resolve(blob);
    }, "image/png");
  });
};

const IMAGE_EXTENSION_BY_MIME_TYPE: Record<string, string> = {
  "image/bmp": "bmp",
  "image/gif": "gif",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
  "image/svg+xml": "svg",
  "image/webp": "webp",
};

const getImageExtensionFromUrl = (imageUrl: string) => {
  try {
    const pathname = new URL(imageUrl).pathname;
    const extension = pathname.split(".").pop()?.trim().toLowerCase();

    if (!extension) {
      return null;
    }

    return extension.replace(/[^a-z0-9]/g, "") || null;
  } catch {
    return null;
  }
};

const getImageExtension = (mimeType: string, imageUrl: string) =>
  IMAGE_EXTENSION_BY_MIME_TYPE[mimeType.toLowerCase()] ??
  getImageExtensionFromUrl(imageUrl) ??
  "png";

export const sanitizeSmileSimulationDownloadFileName = (value: string) =>
  value
    .replace(/\//g, "and")
    .replace(/[\\:*?"<>|#%{}[\]~]/g, "_")
    .replace(/\s+/g, " ")
    .trim();

export const formatSmileSimulationDownloadTimestamp = (
  value: Date | string,
): string => {
  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return formatSmileSimulationDownloadTimestamp(new Date());
  }

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  const seconds = `${date.getSeconds()}`.padStart(2, "0");

  return `${year}${month}${day}_${hours}${minutes}${seconds}`;
};

export const createSmileSimulationImageDownloadFileNameBase = ({
  chartNumber,
  createdAt,
  patientName,
}: {
  chartNumber: string;
  createdAt: Date | string;
  patientName: string;
}) =>
  sanitizeSmileSimulationDownloadFileName(
    `${patientName}_${chartNumber}_${formatSmileSimulationDownloadTimestamp(
      createdAt,
    )}`,
  );

export const createSmileSimulationExperienceImageDownloadFileNameBase = (
  createdAt: Date | string,
) => `JustScan_Smile_${formatSmileSimulationDownloadTimestamp(createdAt)}`;

export const downloadSmileSimulationImageBlob = async ({
  fileNameBase,
  imageBlob,
  imageUrl,
}: {
  fileNameBase: string;
  imageBlob: Blob;
  imageUrl: string;
}) => {
  const extension = getImageExtension(imageBlob.type, imageUrl);
  const downloadUrl = URL.createObjectURL(imageBlob);
  const downloadLink = document.createElement("a");

  downloadLink.href = downloadUrl;
  downloadLink.download = `${fileNameBase}.${extension}`;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
  URL.revokeObjectURL(downloadUrl);
};

export const downloadSmileSimulationImage = async ({
  fileNameBase,
  imageUrl,
}: {
  fileNameBase: string;
  imageUrl: string;
}) => {
  const response = await fetch(imageUrl);

  if (!response.ok) {
    throw new Error(`Image download failed with status ${response.status}`);
  }

  const imageBlob = await response.blob();

  await downloadSmileSimulationImageBlob({
    fileNameBase,
    imageBlob,
    imageUrl,
  });
};

const loadSmileSimulationImageElementFromUrl = async (url: string) => {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Image request failed with status ${response.status}`);
  }

  const imageBlob = await response.blob();
  const objectUrl = URL.createObjectURL(imageBlob);

  return new Promise<{
    image: HTMLImageElement;
    mimeType: string;
  }>((resolve, reject) => {
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({
        image,
        mimeType: imageBlob.type,
      });
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error(`Failed to load image from ${url}`));
    };

    image.src = objectUrl;
  });
};

const drawSmileSimulationWatermark = ({
  context,
  drawHeight,
  drawWidth,
  watermark,
  x,
  y,
}: {
  context: CanvasRenderingContext2D;
  drawHeight: number;
  drawWidth: number;
  watermark: HTMLImageElement;
  x: number;
  y: number;
}) => {
  context.drawImage(watermark, x, y, drawWidth, drawHeight);
};

const getSmileSimulationWatermarkPlacement = ({
  imageHeight,
  imageWidth,
  watermark,
}: {
  imageHeight: number;
  imageWidth: number;
  watermark: HTMLImageElement;
}) => {
  const padding = Math.max(
    Math.round(Math.min(imageWidth, imageHeight) * 0.03),
    16,
  );
  const maxWidth = imageWidth * 0.24;
  const maxHeight = imageHeight * 0.16;
  const widthScale = maxWidth / watermark.naturalWidth;
  const heightScale = maxHeight / watermark.naturalHeight;
  const scale = Math.min(widthScale, heightScale, 1);
  const drawWidth = Math.max(1, Math.round(watermark.naturalWidth * scale));
  const drawHeight = Math.max(1, Math.round(watermark.naturalHeight * scale));

  return {
    drawHeight,
    drawWidth,
    padding,
  };
};

export const createSmileSimulationWatermarkedImageBlob = async ({
  clinicWatermarkUrl,
  imageUrl,
  serviceWatermarkUrl,
}: {
  clinicWatermarkUrl?: string | null;
  imageUrl: string;
  serviceWatermarkUrl?: string | null;
}) => {
  const [{ image: baseImage, mimeType }, serviceWatermark, clinicWatermark] =
    await Promise.all([
      loadSmileSimulationImageElementFromUrl(imageUrl),
      serviceWatermarkUrl
        ? loadSmileSimulationImageElementFromUrl(serviceWatermarkUrl).then(
            ({ image }) => image,
          )
        : Promise.resolve(null),
      clinicWatermarkUrl
        ? loadSmileSimulationImageElementFromUrl(clinicWatermarkUrl).then(
            ({ image }) => image,
          )
        : Promise.resolve(null),
    ]);

  const canvas = document.createElement("canvas");
  canvas.width = baseImage.naturalWidth;
  canvas.height = baseImage.naturalHeight;

  const context = canvas.getContext("2d");

  if (!context) {
    throw new Error("Failed to initialize watermark canvas");
  }

  context.drawImage(baseImage, 0, 0, canvas.width, canvas.height);

  if (serviceWatermark) {
    const { drawHeight, drawWidth, padding } =
      getSmileSimulationWatermarkPlacement({
        imageHeight: canvas.height,
        imageWidth: canvas.width,
        watermark: serviceWatermark,
      });

    drawSmileSimulationWatermark({
      context,
      drawHeight,
      drawWidth,
      watermark: serviceWatermark,
      x: padding,
      y: canvas.height - padding - drawHeight,
    });
  }

  if (clinicWatermark) {
    const { drawHeight, drawWidth, padding } =
      getSmileSimulationWatermarkPlacement({
        imageHeight: canvas.height,
        imageWidth: canvas.width,
        watermark: clinicWatermark,
      });

    drawSmileSimulationWatermark({
      context,
      drawHeight,
      drawWidth,
      watermark: clinicWatermark,
      x: canvas.width - padding - drawWidth,
      y: canvas.height - padding - drawHeight,
    });
  }

  const outputMimeType = mimeType.startsWith("image/") ? mimeType : "image/png";

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Failed to create watermarked image"));
          return;
        }

        resolve(blob);
      },
      outputMimeType,
      outputMimeType === "image/jpeg" ? 0.92 : undefined,
    );
  });
};

export const getSmileSimulationCapturedImageDataUrl = (
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
  options?: {
    shouldMirror?: boolean;
  },
) => {
  if (!video.videoWidth || !video.videoHeight) {
    return null;
  }

  return drawSmileSimulationSourceToCanvas({
    canvas,
    shouldMirror: options?.shouldMirror ?? true,
    source: video,
    sourceHeight: video.videoHeight,
    sourceWidth: video.videoWidth,
  });
};

export const getSmileSimulationCaptureBounds = (
  sourceWidth: number,
  sourceHeight: number,
): SmileSimulationCaptureBounds => {
  const targetWidth = SMILE_SIMULATION_CAPTURE_WIDTH;
  const targetHeight = SMILE_SIMULATION_CAPTURE_HEIGHT;
  const targetAspectRatio = targetWidth / targetHeight;
  const sourceAspectRatio = sourceWidth / sourceHeight;

  if (sourceAspectRatio > targetAspectRatio) {
    const sourceCropWidth = sourceHeight * targetAspectRatio;

    return {
      sourceX: (sourceWidth - sourceCropWidth) / 2,
      sourceY: 0,
      sourceWidth: sourceCropWidth,
      sourceHeight,
      targetWidth,
      targetHeight,
    };
  }

  const sourceCropHeight = sourceWidth / targetAspectRatio;

  return {
    sourceX: 0,
    sourceY: (sourceHeight - sourceCropHeight) / 2,
    sourceWidth,
    sourceHeight: sourceCropHeight,
    targetWidth,
    targetHeight,
  };
};

export const getSmileSimulationFrameSize = (
  availableWidth: number,
  availableHeight: number,
): SmileSimulationFrameSize => {
  if (availableWidth <= 0 || availableHeight <= 0) {
    return {
      width: 0,
      height: 0,
    };
  }

  const width = Math.min(
    availableWidth,
    availableHeight * SMILE_SIMULATION_FRAME_ASPECT_RATIO,
  );

  return {
    width,
    height: width / SMILE_SIMULATION_FRAME_ASPECT_RATIO,
  };
};
